import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import { Box, CircularProgress, IconButton, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { MyLocation } from '@mui/icons-material';

const PALERMO = [38.13, 13.34];

/**
 * All'apertura, se il permesso del GPS c'è già, la mappa parte da dove si è (chi censisce
 * a Trapani non deve trascinarla da Palermo ogni volta). Se il permesso non c'è non lo si
 * chiede qui: resta Palermo, e c'è il pulsante "Centra su di me".
 */
function PartenzaDaQui({ onPosizione }) {
  const map = useMap();
  useEffect(() => {
    let annullato = false;
    navigator.permissions?.query({ name: 'geolocation' }).then((p) => {
      if (p.state !== 'granted' || annullato) return;
      navigator.geolocation.getCurrentPosition((pos) => {
        if (annullato) return;
        const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        onPosizione?.(c);
        map.setView([c.lat, c.lon], 14);
      }, () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    }).catch(() => {});
    return () => { annullato = true; };
  }, [map]);   // eslint-disable-line react-hooks/exhaustive-deps -- solo all'apertura della mappa
  return null;
}

export default function MapView({ items, onOpen, setMap, cluster, setCluster, userPos, height, onPosizione, onErrore,
                                  vedeVoto = true }) {
  const [mappa, setMappa] = useState(null);
  const [cerco, setCerco] = useState(false);
  const refMappa = useCallback((m) => { setMappa(m); setMap?.(m); }, [setMap]);

  const centraSuDiMe = () => {
    if (!navigator.geolocation) { onErrore?.('Geolocalizzazione non disponibile su questo dispositivo'); return; }
    setCerco(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      setCerco(false);
      const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
      onPosizione?.(c);
      mappa?.setView([c.lat, c.lon], Math.max(mappa.getZoom(), 15));
    }, () => {
      setCerco(false);
      onErrore?.('Posizione non disponibile: abilita il GPS per questo sito e riprova');
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 });
  };

  const markers = useMemo(() => items
    .filter(({ raw }) => Number.isFinite(raw.latitude) && Number.isFinite(raw.longitude))
    .map(({ raw, meta }) => (
    <CircleMarker
      key={raw.id}
      center={[raw.latitude, raw.longitude]}
      radius={9}
      // verde/arancio = revisionato/da rivedere, che dipende dal voto: chi non lo vede ha un colore unico
      pathOptions={!vedeVoto
        ? { color: '#1e40af', fillColor: '#3b82f6', fillOpacity: 0.85, weight: 2 }
        : {
          color: meta.stato === 'revisionato' ? '#15803d' : '#ea580c',
          fillColor: meta.stato === 'revisionato' ? '#22c55e' : '#fb923c',
          fillOpacity: 0.85, weight: 2,
        }}
      eventHandlers={{ click: () => onOpen(raw.id) }}
    >
      <LTooltip direction="top" offset={[0, -6]}>{raw.title || `id ${raw.id}`}</LTooltip>
    </CircleMarker>
  )), [items, onOpen, vedeVoto]);

  return (
    <div style={{ position: 'relative', height }}>
      <MapContainer ref={refMappa} center={PALERMO} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <PartenzaDaQui onPosizione={onPosizione} />
        {cluster
          ? <MarkerClusterGroup chunkedLoading maxClusterRadius={45}>{markers}</MarkerClusterGroup>
          : markers}
        {userPos && (
          <CircleMarker center={[userPos.lat, userPos.lon]} radius={8}
                        pathOptions={{ color: '#ffffff', fillColor: '#1d4ed8', fillOpacity: 1, weight: 3 }}>
            <LTooltip direction="top" offset={[0, -6]}>Sei qui</LTooltip>
          </CircleMarker>
        )}
      </MapContainer>
      {/* sotto i pulsanti + e − di Leaflet, con lo stesso aspetto */}
      <Box sx={{ position: 'absolute', left: 10, top: 84, zIndex: 1000, bgcolor: 'white', borderRadius: '4px',
                 border: '2px solid rgba(0,0,0,0.2)', backgroundClip: 'padding-box' }}>
        <Tooltip title="Centra su di me" placement="right">
          <IconButton size="small" onClick={centraSuDiMe} disabled={cerco} aria-label="Centra la mappa sulla mia posizione"
                      sx={{ borderRadius: '2px', width: 30, height: 30, color: '#1d4ed8' }}>
            {cerco ? <CircularProgress size={16} /> : <MyLocation sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Paper sx={{ position: 'absolute', left: 10, bottom: 24, zIndex: 1000, px: 1.2, py: 0.3 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Cluster</Typography>
          <Switch size="small" checked={cluster} onChange={(e) => setCluster(e.target.checked)} />
        </Stack>
      </Paper>
    </div>
  );
}
